import {
    Button,
    ButtonToolbar,
    DatePicker,
    Divider,
    Form,
    SelectPicker, toaster,
} from "rsuite";
import React, {forwardRef, useCallback, useEffect, useMemo, useState} from "react";
import {submitBasicQuery, submitExternalAPIQuery} from "../../../../service/queryService";
import EducationSearchForm from "./forms/EducationSearchForm";
import {
    basicQueryModel,
    certsSubFormModel,
    degreeSubFormModel,
    experienceSubFormModel, externalApiSubFormModel,
    skillsSubFormModel, yearsOfExpSubFormModel
} from "../../../form/models";
import BasicGrid from "../../../grid/BasicGrid";
import SkillSearchForm from "./forms/SkillSearchForm";
import CertificationsSearchForm from "./forms/CertificationsSearchForm";
import ExperienceSearchForm from "./forms/ExperienceSearchForm";
import ErrorNotification from "../../../notifications/ErrorNotification";
import ReportingHitDetailModal from "./ReportingHitDetailModal";
import ExternalApiQueryForm from "./forms/ExternalApiQueryForm";
import YearsOfExpSearchForm from "./forms/YearsOfExpSearchForm";

const QUERY_TYPES = ['Years of Experience', 'Certifications', 'Education', 'Experience (Company)', 'Skills', 'ExternalAPI'];

const startDateInstance = forwardRef((props, ref) =>
    <DatePicker {...props} format="yyyy-MM" oneTap ranges={[]} style={{width: 200}} ref={ref}/>
);
const endDateInstance = forwardRef((props, ref) =>
    <DatePicker {...props} format="yyyy-MM" oneTap ranges={[]} style={{width: 200}} ref={ref}/>
);
const defaultColumns = [
    {
        key: 'lastName',
        label: 'Last Name',
    },
    {
        key: 'firstName',
        label: 'First Name',
    },
    {
        key: 'matchingContent',
        label: 'Matching Content',
        flexGrow: 2,
    },
];

const pageStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
}

const BasicReporting = () => {

    const [localState, setLocalState] = useState();
    const [queryTypes, setQueryTypes] = useState([]);
    const [querySubForm, setQuerySubform] = useState();
    const [queryResults, setQueryResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hitModalOpen, setHitModalOpen] = useState(false);
    const [selectedUserHit, setSelectedUserHit] = useState({});

    const setClickedUser = useCallback((rowData) => {
        setHitModalOpen(true);
        setSelectedUserHit(rowData);
    },[]);

    const handleModalClose = useCallback(() => {
        setHitModalOpen(false);
    },[]);

    // rsuite does not have an onSubmit, we must handle state changes per controlled field
    const handleFormChange = useCallback((event) => {
        if (event.queryType !== querySubForm) { // clear state on type change
            setLocalState({});
            setLocalState({queryType: event.queryType});
            setQuerySubform(event.queryType);
            setQueryResults([]);
        } else {
            setLocalState({...localState, ...event});
        }
    }, [localState, querySubForm]);

    const submitQuery = useCallback((isFormValid) => {
        if(!isFormValid) return;
        let hasError = false;
        let message = "Must submit at least one field for search";
        switch(localState.queryType) {
            case 'Years of Experience':
                message = "YoE query requires all fields to be filled out";
                hasError = yearsOfExpSubFormModel.check(localState).yoe.hasError ||
                    yearsOfExpSubFormModel.check(localState).operation.hasError;
                break;
            case 'Certifications':
                hasError = certsSubFormModel.check(localState).certName.hasError;
                break;
            case 'Education':
                // education needs only one field to search on, if we have at least one, ignore the rest
                // TODO look at this logic later, it doesn't make sense - but works as expected
                const validationResult = degreeSubFormModel.check(localState);
                Object.keys(validationResult).some((key) => {
                    hasError = validationResult[key].hasError;
                    return hasError === false;
                });
                break;
            case 'Experience (Company)':
                hasError = experienceSubFormModel.check(localState).company.hasError;
                break;
            case 'Skills':
                hasError = skillsSubFormModel.check(localState).skillName.hasError;
                break;
            case 'ExternalAPI':
                const externalApiValidationResult = externalApiSubFormModel.check(localState);
                message = "ExternalAPI query requires all fields to be filled out";
                // requires all fields, any one failure is a failure
                hasError = Object.keys(externalApiValidationResult).some((key) => {
                    return externalApiValidationResult[key].hasError === true;
                });
                break;
            default:
                break;
        }
        if (hasError){
            toaster.push(<ErrorNotification message={message} />, { placement: 'topCenter' });
            return;
        }

        setLoading(true);
        // externalAPI calls are less nested
        if (localState.queryType === 'ExternalAPI') {
            let payload = {...localState};
            delete payload.queryType;
            submitExternalAPIQuery(payload).then((result) => {
                setQueryResults(result);
                setLoading(false);
            });
        }
        else {
            // basic query interface
            // throw all of the random fields into a params map for the backend to inspect
            let payload = {queryType: localState.queryType, params: {...localState}};
            delete payload.params.queryType;
            submitBasicQuery(payload).then((result) => {
                setQueryResults(result);
                setLoading(false);
            });
        }
    }, [localState]);

    // format data for drop down boxes so rsuite is happy
    useEffect(() => {
        setQueryTypes(QUERY_TYPES.map(ele => ({label: ele, value: ele})));
    }, []);

    const subForm = useCallback(() => {
        switch(querySubForm) {
            case 'Years of Experience':
                return <YearsOfExpSearchForm />;
            case 'Certifications':
                return <CertificationsSearchForm />;
            case 'Education':
                return <EducationSearchForm />;
            case 'Experience (Company)':
                return <ExperienceSearchForm />;
            case 'Skills':
                return <SkillSearchForm />;
            case 'ExternalAPI':
                return <ExternalApiQueryForm />;
            default:
                return null;
        }
    }, [querySubForm]);

  return useMemo(() => {
      return (
          <div style={pageStyle}>
              <div style={{float: 'none', marginRight: '20px'}}>
              <Form
                  formValue={localState}
                  layout="horizontal"
                  model={basicQueryModel}
                  onChange={handleFormChange}
                  onError={(error) => console.log(error)}
                  onSubmit={submitQuery}
              >
                  <Form.Group controlId="queryType">
                      <Form.ControlLabel>Query Type</Form.ControlLabel>
                      <Form.Control name="queryType"
                                    accepter={SelectPicker}
                                    data={queryTypes}
                      />
                      <Form.HelpText tooltip>Selecting a type will display additional form elements</Form.HelpText>
                  </Form.Group>
                  <Divider />
                  {subForm()}
                  <Divider />
                  {querySubForm === "Education" || querySubForm === "Experience" ?
                      <>
                        <Form.Group>
                            <Form.ControlLabel>Start Date</Form.ControlLabel>
                            <Form.Control name="startDate" accepter={startDateInstance}/>
                            <Form.HelpText tooltip>Date Range is optional</Form.HelpText>
                        </Form.Group>
                        <Form.Group>
                            <Form.ControlLabel>EndDate</Form.ControlLabel>
                            <Form.Control name="endDate" accepter={endDateInstance}/>
                        </Form.Group>
                      </>
                      : null
                  }
                  <Form.Group>
                      <ButtonToolbar>
                          <Button appearance="primary" type="submit">Submit Query</Button>
                      </ButtonToolbar>
                  </Form.Group>
              </Form>
              </div>
              <div>
                  <p style={{marginBottom: '10px'}}>Clicking on a result row below will show detailed match information.</p>
                  <BasicGrid
                      loading={loading}
                      columns={defaultColumns}
                      data={queryResults}
                      handleClick={setClickedUser}
                      exportTitle={`${querySubForm}-query`}
                  />
              </div>

              <ReportingHitDetailModal
                  open={hitModalOpen}
                  handleClose={handleModalClose}
                  userHit={selectedUserHit}
              />
        </div>
      );
  }, [
      handleFormChange,
      handleModalClose,
      hitModalOpen,
      loading,
      localState,
      queryResults,
      querySubForm,
      queryTypes,
      selectedUserHit,
      setClickedUser,
      subForm,
      submitQuery
  ]);
};

export default BasicReporting;